import { service } from '@ember/service';
import { capitalize } from '@ember/string';
import { cached, tracked } from '@glimmer/tracking';

import moment from 'moment';

import type {
	CreateRecordProperties,
	DSSetup,
	Recipient,
	RecordData,
	Service,
	Slack,
	Timeframe,
} from 'webapp-types';
import type {
	GoalCalculationStrategy,
	GoalDeliveryType,
	GoalOwnerScheduleType,
	ScheduleType,
} from 'webapp/enums/enums';
import {
	DeliveryType,
	GoalFormatType,
	GoalProgressType,
	GoalTargetSuccessCondition,
	GoalTargetValueType,
	GoalType,
	MetricValueType,
	ModelName,
	TimeUnit,
	UserAccess,
} from 'webapp/enums/enums';
import { copyObject } from 'webapp/utils/common';
import {
	CalculationStrategyForMetricType,
	getGoalRangeTimeUnitForPrimaryGranulation,
	MetricTypeForCalculationStrategy,
	MetricTypeForGoalCalculations,
	PeriodIdFormats,
	TargetEndDateFormat,
} from 'webapp/utils/goal-value-rows';
import { getCurrentUnixTimestamp, getFormattedTime, isDurationFormat } from 'webapp/utils/number';
import type { ValidationRule } from 'webapp/utils/validate';

import type { Group } from 'webapp/models-custom';
import BufferedModel from 'webapp/models-custom/buffer/buffered-model';
import DateRange from 'webapp/models-custom/date-range';
import GoalData from 'webapp/models-custom/goal-data';
import type Metric from 'webapp/models-custom/metric';
import MetricSetup from 'webapp/models-custom/metric-setup';
import type {
	OnChangeProp as MetricSetupOnChangeProp,
	OnChangePropValue as MetricSetupOnChangePropValue,
} from 'webapp/models-custom/metric-setup-base';
import type GoalRecord from 'webapp/models/goal';

import type { GoalRangeTimeUnit } from 'webapp/services/model-manager/goals';

export type HistoryValues = Partial<Record<GoalRangeTimeUnit, Record<string, number | null>>>;

export interface MetricDefinition {
	metricType?: MetricValueType;
}

export interface SubGoal {
	goalId: number;
	blockId?: number; // empty when this is not saved yet
	goalFormat: GoalFormatType;
	isTimeframeValid: boolean;
}

export const MAX_TARGET_VALUE_LENGTH = 16;

export default class Goal extends BufferedModel<GoalRecord, RecordData.Goal.Serialized> {
	@service('logger') declare loggerService: Service.Logger;
	@service('timer') declare timerService: Service.Timer;
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('infolayer') declare infolayer: Service.Infolayer;
	@service('account') declare accountService: Service.Account;
	@service('billing') declare billingService: Service.Billing;
	@service('model-manager/goals') declare mmGoalsService: Service.ModelManager.Goals;
	@service('model-manager/users') declare mmUsersService: Service.ModelManager.Users;
	@service('model-manager/metrics') declare mmMetricsService: Service.ModelManager.Metrics;
	@service('model-manager/tags') declare mmTagsService: Service.ModelManager.Tags;
	@service('model-manager/blocks') declare mmBlocksService: Service.ModelManager.Blocks;
	@service('model-manager/samples') declare mmSamplesService: Service.ModelManager.Samples;
	@service('model-manager/data-performance-summaries')
	declare mmDataPerformanceSummariesService: Service.ModelManager.DataPerformanceSummaries;
	@service('request') declare requestService: Service.Request;
	@service('model-manager/groups') declare mmGroupsService: Service.ModelManager.Groups;
	@service('timeframe') declare timeframeService: Service.Timeframe;

	// ------- props from / to ED record ------

	get id() {
		return this.model.id;
	}

	get spaceId(): number {
		return this.model.spaceId;
	}

	get description(): string {
		return this.getBuffered('description');
	}

	set description(value: string) {
		this.setBuffered('description', value);
	}

	get type(): GoalType {
		return this.getBuffered('type');
	}

	set type(value: GoalType) {
		this.setBuffered('type', value);
	}

	get title(): string {
		return this.getBuffered('title');
	}

	set title(value: string) {
		this.setBuffered('title', value);
	}

	get progressType(): GoalProgressType {
		return this.getBuffered('progressType');
	}

	set progressType(value: GoalProgressType) {
		this.setBuffered('progressType', value);
	}

	get ownerId(): number | null {
		return this.getBuffered('ownerId');
	}

	set ownerId(value: number | null) {
		this.setBuffered('ownerId', value);
	}

	get groupId(): string | null {
		return this.getBuffered('groupId');
	}

	set groupId(value: string | null) {
		this.setBuffered('groupId', value);
	}

	get tags(): number[] {
		return this.getBuffered('tags');
	}

	set tags(value: number[]) {
		this.setBuffered('tags', value);
	}

	get order(): number {
		return this.getBuffered('order');
	}

	set order(value: number) {
		this.setBuffered('order', value);
	}

	get blockId(): number | null {
		return this.getBuffered('blockId');
	}

	set blockId(value: number | null) {
		this.setBuffered('blockId', value);
	}

	get favorite(): boolean {
		return this.getBuffered('favorite');
	}

	set favorite(value: boolean) {
		this.setBuffered('favorite', value);
	}

	get source(): RecordData.Goal.Source {
		// used in mobile pages where we have no metrics loaded
		return this.model.source;
	}

	get isArchived(): boolean {
		return this.getBuffered('isArchived');
	}

	set isArchived(value: boolean) {
		this.setBuffered('isArchived', value);
	}

	get goalKeyLong(): string {
		return this.model.goalKeyLong;
	}

	get subGoals(): SubGoal[] {
		return this.getBuffered('subGoals');
	}

	set subGoals(value: SubGoal[]) {
		this.setBuffered('subGoals', value);
	}

	get primaryGranulation(): GoalRangeTimeUnit {
		return this.getBuffered('primaryGranulation');
	}

	set primaryGranulation(value: GoalRangeTimeUnit) {
		this.setBuffered('primaryGranulation', value);
	}

	get targetValueType(): GoalTargetValueType {
		return this.getBuffered('targetValueType');
	}

	set targetValueType(value: GoalTargetValueType) {
		this.setBuffered('targetValueType', value);
	}

	get targetValue(): number | null {
		return this.getBuffered('targetValue');
	}

	set targetValue(value: number | null) {
		this.setBuffered('targetValue', value);
	}

	get endDate(): string | null {
		return this.getBuffered('endDate');
	}

	set endDate(value: string | null) {
		this.setBuffered('endDate', value);
	}

	get historyValues(): HistoryValues {
		return this.getBuffered('historyValues');
	}

	set historyValues(value: HistoryValues) {
		this.setBuffered('historyValues', value);
	}

	get historyValuesOverride(): boolean {
		return this.getBuffered('historyValuesOverride');
	}

	set historyValuesOverride(value: boolean) {
		this.setBuffered('historyValuesOverride', value);
	}

	get calculationStrategyOverride(): GoalCalculationStrategy | null {
		return this.getBuffered('calculationStrategyOverride');
	}

	set calculationStrategyOverride(value: GoalCalculationStrategy | null) {
		this.setBuffered('calculationStrategyOverride', value);
	}

	get statusOverride(): boolean {
		return this.getBuffered('statusOverride');
	}

	set statusOverride(value: boolean) {
		this.setBuffered('statusOverride', value);
	}

	get timeframe(): Timeframe | null {
		return this.getBuffered('timeframe');
	}

	set timeframe(value: Timeframe | null) {
		this.setBuffered('timeframe', value);
	}

	get targetSuccessCondition(): GoalTargetSuccessCondition | null {
		return this.getBuffered('targetSuccessCondition');
	}

	set targetSuccessCondition(value: GoalTargetSuccessCondition | null) {
		this.setBuffered('targetSuccessCondition', value);
	}

	get decimals(): number | null {
		return this.getBuffered('decimals');
	}

	set decimals(value: number | null) {
		this.setBuffered('decimals', value);
	}

	get formatType(): GoalFormatType | null {
		return this.getBuffered('formatType');
	}

	set formatType(value: GoalFormatType | null) {
		this.setBuffered('formatType', value);
	}

	get formatUnit(): string | null {
		return this.getBuffered('formatUnit');
	}

	set formatUnit(value: string | null) {
		this.setBuffered('formatUnit', value);
	}

	get startValue(): number | null {
		return this.getBuffered('startValue');
	}

	set startValue(value: number | null) {
		this.setBuffered('startValue', value);
	}

	get mainMetricSetupKey(): string | null {
		return this.getBuffered('mainMetricSetupKey');
	}

	set mainMetricSetupKey(value: string | null) {
		this.setBuffered('mainMetricSetupKey', value);
	}

	get mainMetricSetupSpaceAccessId(): number | null {
		return this.getBuffered('mainMetricSetupSpaceAccessId');
	}

	set mainMetricSetupSpaceAccessId(value: number | null) {
		this.setBuffered('mainMetricSetupSpaceAccessId', value);
	}

	get mainMetricSetupDimensions(): string[] {
		return this.getBuffered('mainMetricSetupDimensions');
	}

	set mainMetricSetupDimensions(value: string[]) {
		this.setBuffered('mainMetricSetupDimensions', value);
	}

	get mainMetricSetupFilters(): DSSetup.MetricSetupFiltersData | null {
		return this.getBuffered('mainMetricSetupFilters');
	}

	set mainMetricSetupFilters(value: DSSetup.MetricSetupFiltersData | null) {
		this.setBuffered('mainMetricSetupFilters', value);
	}

	get otherMetricSetupKey(): string | null {
		return this.getBuffered('otherMetricSetupKey');
	}

	set otherMetricSetupKey(value: string | null) {
		this.setBuffered('otherMetricSetupKey', value);
	}

	get otherMetricSetupSpaceAccessId(): number | null {
		return this.getBuffered('otherMetricSetupSpaceAccessId');
	}

	set otherMetricSetupSpaceAccessId(value: number | null) {
		this.setBuffered('otherMetricSetupSpaceAccessId', value);
	}

	get otherMetricSetupDimensions(): string[] {
		return this.getBuffered('otherMetricSetupDimensions');
	}

	set otherMetricSetupDimensions(value: string[]) {
		this.setBuffered('otherMetricSetupDimensions', value);
	}

	get otherMetricSetupFilters(): DSSetup.MetricSetupFiltersData | null {
		return this.getBuffered('otherMetricSetupFilters');
	}

	set otherMetricSetupFilters(value: DSSetup.MetricSetupFiltersData | null) {
		this.setBuffered('otherMetricSetupFilters', value);
	}

	get access(): UserAccess {
		return this.getBuffered('access');
	}

	set access(value: UserAccess) {
		this.setBuffered('access', value);
	}

	get users(): number[] {
		return this.getBuffered('users');
	}

	set users(value: number[]) {
		this.setBuffered('users', value);
	}

	get isDeliveryOn(): boolean {
		return this.getBuffered('isDeliveryOn');
	}

	set isDeliveryOn(value: boolean) {
		this.setBuffered('isDeliveryOn', value);
	}

	get delivery(): GoalDeliveryType[] {
		return this.getBuffered('delivery');
	}

	set delivery(value: GoalDeliveryType[]) {
		this.setBuffered('delivery', value);
	}

	get scheduleTypes(): ScheduleType[] {
		return this.getBuffered('scheduleTypes');
	}

	set scheduleTypes(value: ScheduleType[]) {
		this.setBuffered('scheduleTypes', value);
	}

	get slackChannels(): Slack.Channel[] {
		return this.getBuffered('slackChannels');
	}

	set slackChannels(value: Slack.Channel[]) {
		this.setBuffered('slackChannels', value);
	}

	get recipients(): string[] {
		return this.getBuffered('recipients');
	}

	set recipients(value: string[]) {
		this.setBuffered('recipients', value);
	}

	get ownerScheduleType(): GoalOwnerScheduleType | null {
		return this.getBuffered('ownerScheduleType');
	}

	set ownerScheduleType(value: GoalOwnerScheduleType | null) {
		this.setBuffered('ownerScheduleType', value);
	}

	get created(): number {
		return this.model.created || getCurrentUnixTimestamp();
	}

	// -------------- general goal setup -----------

	get isGoalType() {
		return this.type === GoalType.GOAL;
	}

	get isObjectiveType() {
		return this.type === GoalType.OBJECTIVE;
	}

	get isAutomaticGoal() {
		return this.progressType === GoalProgressType.AUTOMATIC;
	}

	get isManualGoal() {
		return this.progressType === GoalProgressType.MANUAL;
	}

	get isSubgoalsProgressGoal() {
		return this.progressType === GoalProgressType.SUBGOALS;
	}

	override get validationRules() {
		const rules: Record<string, ValidationRule[]> = {
			title: ['required'],
		};

		if (!this.isTargetOtherMetric) {
			rules.targetValue = ['required'];
		}

		if (this.isAutomaticGoal) {
			rules.mainMetricSetupKey = ['required'];

			if (this.mainMetricSetup.allowBasicDimensionValueFilters) {
				rules.mainMetricAttributeName = ['required'];
			}

			if (this.mainMetricSetup.allowUnitNameFilter) {
				rules.mainMetricUnitName = ['required'];
			}
		} else if (this.isManualGoal) {
			rules.startValue = ['required'];
		}

		return rules;
	}

	get isForCurrentSpace() {
		return !this.spaceId || this.spaceId === this.accountService.id;
	}

	@cached
	get mainMetricSetup(): MetricSetup {
		return new MetricSetup(
			{
				key: this.mainMetricSetupKey,
				spaceAccessId: this.mainMetricSetupSpaceAccessId,
				dimensions: this.mainMetricSetupDimensions,
				filters: this.mainMetricSetupFilters,
				allowAllDimensionValues: false,
				allowAllUnits: false,
			},
			this.handleMainMetricSetupUpdate.bind(this),
		);
	}

	get mainMetric(): Metric | null {
		return this.mainMetricSetup?.metric || null;
	}

	get mainMetricAttributeName() {
		// for validation
		return this.mainMetricSetup.firstAttributeNameFromBasicFilters;
	}

	get mainMetricUnitName() {
		// for validation
		return this.mainMetricSetup.unitName;
	}

	get mainMetricType() {
		const override = this.calculationStrategyOverride;
		return override && !this.isManualGoal
			? MetricTypeForCalculationStrategy[override]
			: this.autoMainMetricType;
	}

	get calculationStrategy() {
		return CalculationStrategyForMetricType[this.mainMetricType];
	}

	get autoCalculationStrategy() {
		return CalculationStrategyForMetricType[this.autoMainMetricType];
	}

	@cached
	get autoMainMetricType() {
		// aggregatable (signups) / current (followers) / noreset (churn rate)
		const metric = this.mainMetric;
		const metricType = this.mainMetricValueType;

		let goalMetricType = MetricTypeForGoalCalculations.AGGREGATABLE;
		if (metricType === MetricValueType.CURRENT || this.isManualGoal) {
			goalMetricType = MetricTypeForGoalCalculations.CURRENT;
		} else if (metric?.noReset) {
			goalMetricType = MetricTypeForGoalCalculations.NO_RESET;
		}
		return goalMetricType;
	}

	get mainMetricValueType() {
		return this._mainMetricDefinition?.metricType ?? this.mainMetric?.metricType;
	}

	get isCalculationStrategyOverridden() {
		return !this.isManualGoal && this.calculationStrategyOverride !== null;
	}

	get isSumCalculationStrategyAllowed() {
		const metricType = this.mainMetricValueType;
		const isResetMetric =
			(metricType === MetricValueType.GENERAL || metricType === MetricValueType.EVENT) &&
			!this.mainMetric?.noReset;
		return (
			this.autoMainMetricType === MetricTypeForGoalCalculations.AGGREGATABLE || isResetMetric
		);
	}

	@tracked private _mainMetricDefinition: MetricDefinition | null = null;
	@tracked isMainMetricLimitationsLoading = false;
	// key of the metric whose definition is loaded or loading (null = neither)
	private _mainMetricLimitationsKey: string | null = null;

	loadMainMetricLimitations(): Promise<void> {
		const metricData = this.mainMetricSetup.metricData;

		if (!metricData) {
			this._mainMetricDefinition = null;
			this._mainMetricLimitationsKey = null;
			this.isMainMetricLimitationsLoading = false;
			return Promise.resolve();
		}

		const key = `${metricData.key}-${metricData.spaceAccessId}`;
		if (this._mainMetricLimitationsKey === key && !this.isMainMetricLimitationsLoading) {
			return Promise.resolve();
		}

		this._mainMetricLimitationsKey = key;
		this.isMainMetricLimitationsLoading = true;

		return this.mmMetricsService
			.getMetricLimitations([metricData])
			.then(
				(limitations) => {
					// ignore stale resolutions after the metric changed
					if (this._mainMetricLimitationsKey === key) {
						this._mainMetricDefinition = limitations?.metricDefinition ?? null;
					}
				},
				() => {
					// skip on error — mainMetricType falls back to the metric model
				},
			)
			.finally(() => {
				if (this._mainMetricLimitationsKey === key) {
					this.isMainMetricLimitationsLoading = false;
				}
			});
	}

	// returns true when a load is in progress and `callback` was scheduled to run after it;
	// returns false when limitations are already loaded and the caller can proceed synchronously
	willScheduleAfterMainMetricLimitationsLoaded(callback: () => void): boolean {
		const loaded = this.loadMainMetricLimitations();
		if (this.isMainMetricLimitationsLoading) {
			void loaded.then(callback);
			return true;
		}
		return false;
	}

	// reuse the source goal's already-loaded limitations (e.g. on a copy for the advanced popup)
	copyMainMetricLimitationsFrom(source: Goal): void {
		this._mainMetricDefinition = source._mainMetricDefinition;
		this._mainMetricLimitationsKey = source._mainMetricLimitationsKey;
	}

	get dateRange() {
		return this.primaryDataModel?.dateRange || null;
	}

	@cached
	get sourceCM() {
		return this.mainMetric?.source;
	}

	get isMainMetricPercentage() {
		const format = this.mainMetric?.format || '';
		return format.includes('%') || format.includes('◊');
	}

	get isMainMetricInverted() {
		const metric = this.mainMetric;
		return !!metric && metric.changeInvert;
	}

	get isDurationMetric() {
		const format = this.mainMetric ? this.mainMetric.format : null;
		return !!format && isDurationFormat(format);
	}

	get isTargetValuePercentFormat() {
		return this.targetValueType === GoalTargetValueType.PERCENTAGE;
	}

	get isHistoryValuesPercentFormat() {
		// for inputs
		return this.isMainMetricPercentage || this.formatType === GoalFormatType.PERCENTAGE;
	}

	get isDimensionRequired() {
		return this.mainMetricSetup.mustSelectDimension;
	}

	get isMetricFilled() {
		return this.mainMetricSetup.isFilledSingleOutput;
	}

	get useDurationInput() {
		return this.isDurationMetric && !this.isTargetValuePercentFormat;
	}

	get isTimeframeContinuous() {
		return this.timeframe === null;
	}

	@cached
	get ownerUser() {
		let owner;
		if (this.ownerId) {
			owner = this.mmUsersService.findRecord(String(this.ownerId));
		}
		return owner || null;
	}

	@cached
	get group(): Group | null {
		const groupId = this.groupId;
		return groupId ? this.mmGroupsService.findModel(groupId) : null;
	}

	get goalTypeText() {
		return this.isObjectiveType ? 'objective' : 'goal';
	}

	get favoriteText(): string {
		if (this.favorite) {
			return 'Unfavorite';
		} else {
			return 'Favorite';
		}
	}

	get timeframeText() {
		return this.timeframeService.getTimeframeString(this.timeframe, true);
	}

	get isMine() {
		// we need this for user access popup
		return this.isOwnedByMe;
	}

	get isOwnedByMe() {
		return this.ownerId === this.currentUserService.numericId;
	}

	get canEdit() {
		return (
			this.isForCurrentSpace &&
			(this.isOwnedByMe ||
				this.currentUserService.isAdmin ||
				this.currentUserService.isEditor) &&
			this.billingService.goalsEnabled &&
			!this.currentUserService.isDemo
		);
	}

	get canDelete() {
		return (
			this.isForCurrentSpace &&
			(this.isOwnedByMe || this.currentUserService.isAdmin) &&
			this.billingService.goalsEnabled
		);
	}

	get canEditPermissions(): boolean {
		const { model, isAdmin } = this.currentUserService;

		return (this.isOwnedByMe || isAdmin) && !!model && !model.isViewer;
	}

	get tagsObjs() {
		const tagIds = this.tags || [];
		return this.mmTagsService.savedRecords.filter((tag) => {
			return tagIds.includes(+tag.id!);
		});
	}

	get humanReadableConditionText() {
		if (this.isSubgoalsProgressGoal) {
			return 'Subgoals progress goal';
		} else {
			const successCondition = this.targetSuccessCondition;

			if (!successCondition) {
				return '';
			}

			let text = this.mmGoalsService.getTextForSuccessCondition(successCondition);

			if (this.isTargetOtherMetric) {
				text += ' other metric';
			} else if (typeof this.targetValue === 'number') {
				text += ` ${this.targetValue}`;

				const primaryGranulation = this.primaryGranulation;

				if (primaryGranulation === TimeUnit.TOTAL) {
					if (this.isByEndDateAndNotContinuous) {
						text += ' by end date';
					}
					return text;
				}

				text += ` ${DateRange.getTimeUnitText(primaryGranulation, true)}`;
			}

			return text;
		}
	}

	get successConditionOptions() {
		let options = [
			{
				value: GoalTargetSuccessCondition.REACH,
				label: this.mmGoalsService.getTextForSuccessCondition(
					GoalTargetSuccessCondition.REACH,
				),
				disabled: !this.timeframe && !this.endDate,
			},
		];
		if (this.isAutomaticGoal) {
			options.push({
				value: GoalTargetSuccessCondition.METRIC,
				label: this.mmGoalsService.getTextForSuccessCondition(
					GoalTargetSuccessCondition.METRIC,
					this.mainMetric?.changeInvert,
				),
				disabled: false,
			});
		} else if (this.isManualGoal) {
			options.push({
				value: GoalTargetSuccessCondition.ABOVE,
				label: this.mmGoalsService.getTextForSuccessCondition(
					GoalTargetSuccessCondition.ABOVE,
				),
				disabled: false,
			});
			options.push({
				value: GoalTargetSuccessCondition.BELOW,
				label: this.mmGoalsService.getTextForSuccessCondition(
					GoalTargetSuccessCondition.BELOW,
				),
				disabled: false,
			});
		} else if (this.isSubgoalsProgressGoal) {
			options = [];
		}
		return options;
	}

	checkSuccessCondition(reset = false) {
		// when switching goal types, keep this valid
		// returns true if success condition was changed
		const validOptions = this.successConditionOptions.filter((option) => !option.disabled);
		const selected = this.targetSuccessCondition;

		const selectedOption = validOptions.find((option) => option.value === selected);
		if (!selectedOption) {
			if (
				selected &&
				this.isAutomaticGoal &&
				[GoalTargetSuccessCondition.ABOVE, GoalTargetSuccessCondition.BELOW].includes(
					selected,
				)
			) {
				this.targetSuccessCondition = GoalTargetSuccessCondition.METRIC;
			} else if (
				selected &&
				this.isManualGoal &&
				selected === GoalTargetSuccessCondition.METRIC
			) {
				this.targetSuccessCondition = GoalTargetSuccessCondition.ABOVE;
			} else {
				this.targetSuccessCondition = validOptions[0]?.value || null;
			}
		} else if (reset && !this.isTimeframeContinuous) {
			this.targetSuccessCondition = GoalTargetSuccessCondition.REACH;
		}
	}

	checkPrimaryGranulation() {
		const isByEndDateAllowed = this.useEndDateForCalculations,
			isByEndDateSelected = this.primaryGranulation === TimeUnit.TOTAL;

		if (isByEndDateAllowed && !this.isTimeframeContinuous && !isByEndDateSelected) {
			this.primaryGranulation = TimeUnit.TOTAL;
		}

		if (!isByEndDateAllowed && isByEndDateSelected) {
			this.primaryGranulation = TimeUnit.MONTH;
		}
	}

	private handleMainMetricSetupUpdate(
		prop: MetricSetupOnChangeProp,
		value: MetricSetupOnChangePropValue,
	) {
		switch (prop) {
			case 'key':
				this.mainMetricSetupKey = value as string;
				break;
			case 'spaceAccessId':
				this.mainMetricSetupSpaceAccessId = value as number | null;
				break;
			case 'dimensions':
				this.mainMetricSetupDimensions = value as string[];
				break;
			case 'filters':
				this.mainMetricSetupFilters = value as DSSetup.MetricSetupFiltersData;
				break;
		}
	}

	get measurement() {
		// percent, number, and currencies in one
		if (this.formatType === GoalFormatType.PERCENTAGE) {
			return String(GoalFormatType.PERCENTAGE);
		} else if (this.formatType === GoalFormatType.NUMBER) {
			return String(GoalFormatType.NUMBER);
		} else if (this.formatType === GoalFormatType.CURRENCY) {
			return this.formatUnit;
		}
		return null;
	}

	set measurement(value: string | null) {
		let formatType: GoalFormatType | null = null;
		let formatUnit: string | null = null;
		if (value === String(GoalFormatType.PERCENTAGE)) {
			formatType = GoalFormatType.PERCENTAGE;
		} else if (value === String(GoalFormatType.NUMBER)) {
			formatType = GoalFormatType.NUMBER;
		} else if (value) {
			formatType = GoalFormatType.CURRENCY;
			formatUnit = value;
		}
		this.formatType = formatType;
		this.formatUnit = formatUnit;
	}

	get successConditionIcon(): IconName | undefined {
		switch (this.targetSuccessCondition) {
			case GoalTargetSuccessCondition.REACH:
				return 'ArrowRight';
			case GoalTargetSuccessCondition.METRIC:
				return this.mainMetric?.changeInvert ? 'ArrowDown' : 'ArrowUp';
			case GoalTargetSuccessCondition.ABOVE:
				return 'ArrowUp';
			case GoalTargetSuccessCondition.BELOW:
				return 'ArrowDown';
			default:
				return undefined;
		}
	}

	// -------------- for when goal value is another metric -----------

	@cached
	get otherMetricSetup(): MetricSetup {
		return new MetricSetup(
			{
				key: this.otherMetricSetupKey,
				spaceAccessId: this.otherMetricSetupSpaceAccessId,
				dimensions: this.otherMetricSetupDimensions,
				filters: this.otherMetricSetupFilters,
				allowAllDimensionValues: false,
				allowAllUnits: false,
			},
			this.handleOtherMetricSetupUpdate.bind(this),
		);
	}

	get isTargetOtherMetric() {
		return !!this.otherMetricSetupKey;
	}

	@cached
	get otherMetric(): Metric | null {
		return this.otherMetricSetup?.metric || null;
	}

	get mustSelectOtherMetricAttribute() {
		return !!this.otherMetric?.allowDimensions;
	}

	get mustSelectOtherMetricUnit() {
		return !!this.otherMetric?.hasUnits;
	}

	private handleOtherMetricSetupUpdate(
		prop: MetricSetupOnChangeProp,
		value: MetricSetupOnChangePropValue,
	) {
		switch (prop) {
			case 'key':
				this.otherMetricSetupKey = value as string;
				break;
			case 'spaceAccessId':
				this.otherMetricSetupSpaceAccessId = value as number | null;
				break;
			case 'dimensions':
				this.otherMetricSetupDimensions = value as string[];
				break;
			case 'filters':
				this.otherMetricSetupFilters = value as DSSetup.MetricSetupFiltersData;
				break;
		}
	}

	// ------------ Subgoal hierarchy --------

	@cached
	get children() {
		const ids = this.subGoals.map((item) => item.goalId);
		return this.mmGoalsService.goalModels
			.filter((item) => ids.includes(item.numericId!))
			.sort((a, b) => a.numericId! - b.numericId!);
	}

	get areChildrenSubgoals() {
		return this.isSubgoalsProgressGoal;
	}

	get parentGoal() {
		return this.mmGoalsService.hierarchyMap.get(this.id);
	}

	get parentGoalTimeframe() {
		return this.parentGoal?.timeframe;
	}

	get canHaveChildren() {
		return this.isPublic;
	}

	get canBeAChild() {
		return this.isPublic && !this.isObjectiveType;
	}

	get depthInHierarchy() {
		// depth of the current goal irrelevant of its children
		// 1 without parent, 2 just one parent, 3 parent and its parent, etc.
		let depth = 1;
		let currentGoal = this as Goal;
		while (currentGoal.parentGoal) {
			depth++;
			currentGoal = currentGoal.parentGoal;
		}
		return depth;
	}

	get childrenDepth() {
		// 1 just children, 2 also grandchildren, etc.
		let depth = 0;
		if (this.children.length > 0) {
			depth = Math.max(...this.children.map((child) => child.childrenDepth)) + 1;
		}
		return depth;
	}

	get subGoalData() {
		return this.parentGoal?.subGoals.find((item) => item.goalId === this.numericId);
	}

	// ---------- Data / statuses ----------
	// for both primary block (my/company/teams pages)
	// and hierarchy block (data when this is added as subgoal - can be diff timeframe, so diff data / status)

	get primaryBlockId() {
		return this.blockId;
	}

	private _primaryData: GoalData | null = null;

	@cached
	get primaryDataModel() {
		const blockId = this.primaryBlockId;
		if (blockId) {
			if (this._primaryData?.blockId !== this.primaryBlockId) {
				this._primaryData = new GoalData(this, this.primaryBlockId);
			}
		} else {
			// only possible when goal is not saved yet (no blockId)
			this._primaryData = null;
		}
		return this._primaryData;
	}

	get hierarchyBlockId() {
		return this.parentGoal?.subGoals.find((item) => item.goalId === this.numericId)?.blockId;
	}

	private _hierarchyData: GoalData | null = null;

	@cached
	get hierarchyDataModel() {
		const blockId = this.hierarchyBlockId;
		if (blockId) {
			if (this._hierarchyData?.blockId !== blockId) {
				this._hierarchyData = new GoalData(this, blockId);
			}
		} else {
			this._hierarchyData = null;
		}
		return this._hierarchyData;
	}

	get hierarchyDataModelWithFallback() {
		return this.hierarchyDataModel || this.primaryDataModel;
	}

	// ---------- Other ----------

	setStartValueFromUserInput(value?: number | string | null) {
		const isValueValid = this.mmGoalsService.isValueValid(value);

		const fallbackStringValue = this.isManualGoal ? '0' : null;
		const fallbackModelValue = this.isManualGoal ? 0 : null;

		this.startValue = isValueValid ? Number(value) : fallbackModelValue;

		// return "validated" value to update to input
		return isValueValid ? String(value) : fallbackStringValue;
	}

	private formatHistoryValueKey(key: string | undefined): string | null {
		if (!key) {
			return null;
		}
		const timeUnit = getGoalRangeTimeUnitForPrimaryGranulation(this.primaryGranulation);
		return moment.utc(key, PeriodIdFormats[timeUnit]).format(TargetEndDateFormat);
	}

	get isHistoryValuesValid() {
		if (this.isTargetOtherMetric) {
			return true;
		}

		if (this.isSubgoalsProgressGoal) {
			return true;
		}

		const historyValues = this.historyValues;

		// check if it is empty array or object - some returns false for empty array
		if (Object.keys(historyValues).length === 0) {
			return false;
		}

		return !Object.values(historyValues).some((values) => Object.keys(values).length === 0);
	}

	get isAnyHistoryValueTooBig() {
		return Object.values(this.historyValues).some((values) =>
			Object.keys(values).some((key) => {
				const value = values[key];
				return value !== null && String(value).length > MAX_TARGET_VALUE_LENGTH;
			}),
		);
	}

	@cached
	get endDateTs() {
		return this.mmGoalsService.getTargetEndDateTimestamp(this.endDate);
	}

	private get periodDateRangeFromTimeframe() {
		const timeframe = this.timeframe;
		if (!timeframe) {
			return null;
		}
		const periodsData = this.timeframeService.getTimeframePeriodsFromCache(timeframe);
		return periodsData ? this.timeframeService.findPeriod(periodsData, timeframe.type) : null;
	}

	@cached
	get endDateTsFromTimeframe() {
		return this.periodDateRangeFromTimeframe
			? this.periodDateRangeFromTimeframe.periodEndTS - 1
			: null;
	}

	@cached
	get startDateTsFromTimeframe() {
		return this.periodDateRangeFromTimeframe?.periodStartTS ?? null;
	}

	@cached
	get historyValuePeriodIdKeys(): string[] {
		const timeUnit = getGoalRangeTimeUnitForPrimaryGranulation(this.primaryGranulation);
		const values = this.historyValues[timeUnit];
		if (!values) {
			return [];
		}
		return Object.keys(values).sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
	}

	get startDateFromHistoryValues(): string | null {
		return this.formatHistoryValueKey(this.historyValuePeriodIdKeys[0]);
	}

	get endDateFromHistoryValues(): string | null {
		return this.formatHistoryValueKey(this.historyValuePeriodIdKeys.at(-1));
	}

	get endDateFromTimeframe() {
		const ts = this.endDateTsFromTimeframe;
		return ts ? getFormattedTime(ts, 'UTC', TargetEndDateFormat) : null;
	}

	get startDateFromTimeframe() {
		const ts = this.startDateTsFromTimeframe;
		return ts ? getFormattedTime(ts, 'UTC', TargetEndDateFormat) : null;
	}

	get isReachCondition() {
		return (
			!this.isSubgoalsProgressGoal &&
			this.targetSuccessCondition === GoalTargetSuccessCondition.REACH
		);
	}

	get isAboveOrBelowCondition() {
		return (
			this.targetSuccessCondition === GoalTargetSuccessCondition.ABOVE ||
			this.targetSuccessCondition === GoalTargetSuccessCondition.BELOW
		);
	}

	get isMetricCondition() {
		return this.targetSuccessCondition === GoalTargetSuccessCondition.METRIC;
	}

	get isByEndDateAndNotContinuous() {
		return this.primaryGranulation === TimeUnit.TOTAL && !this.isTimeframeContinuous;
	}

	get useEndDateForCalculations() {
		return this.isReachCondition && !!this.valuesLastDate;
	}

	get valuesLastDate() {
		// the point after which we don't show history values
		// also used in calculations when isReachCondition = true
		return this.endDate || this.endDateFromTimeframe;
	}

	get valuesLastDateTs() {
		// the point after which we don't show history values
		// also used in calculations when isReachCondition = true
		return this.endDateTs || this.endDateTsFromTimeframe;
	}

	get deliveryTimeText() {
		return this.mmGoalsService
			.getScheduleTypeArrayOfString(this)
			.map((text) => capitalize(text))
			.join(', ');
	}

	get isPublic() {
		return this.access === UserAccess.PUBLIC;
	}

	get mappedRecipients(): Recipient[] {
		return this.mmUsersService.getMappedRecipients(this.recipients, [], this.slackChannels);
	}

	changeAccess(newAccess: UserAccess, usersPermissionIds: number[] = []): boolean {
		if (this.accountService.handleDemo()) {
			return true;
		}

		let selectedUsers: number[] = [];

		switch (newAccess) {
			case UserAccess.PRIVATE:
				if (!this.isOwnedByMe) {
					this.infolayer.error('You can set to private only goals you have created!');
					return false;
				}
				break;
			case UserAccess.RESTRICTED:
				selectedUsers = usersPermissionIds;
				break;
			case UserAccess.PUBLIC:
				break;
			default:
				this.loggerService.logError('Unrecognized goal access!', {
					goalId: this.id,
					goalAccess: newAccess,
				});
				return false;
		}

		this.access = newAccess;

		this.users = selectedUsers;

		return true;
	}

	setDefaultProgressAndFormatType(canUseAdvancedGoals: boolean) {
		if (this.isObjectiveType) {
			this.progressType = GoalProgressType.SUBGOALS;
		} else {
			this.progressType = canUseAdvancedGoals
				? GoalProgressType.AUTOMATIC
				: GoalProgressType.MANUAL;
		}

		this.formatType = this.isManualGoal ? GoalFormatType.PERCENTAGE : null;
	}

	setDefaultDeliverySettings() {
		this.setDefaultScheduleTypeOnPrimaryGranulationChange();

		this.recipients = [];
		this.slackChannels = [];
		if (this.isManualGoal) {
			this.delivery = [];
			this.scheduleTypes = [];
		} else {
			this.delivery = [DeliveryType.EMAIL];
			this.ownerScheduleType = null;
		}
	}

	setDefaultScheduleTypeOnPrimaryGranulationChange() {
		if (this.isManualGoal) {
			this.ownerScheduleType =
				this.mmGoalsService.getGoalOwnerScheduleTypeForPrimaryGranulation(
					this.primaryGranulation,
				);
		} else {
			this.scheduleTypes = [
				this.mmGoalsService.getScheduleTypeForPrimaryGranulation(this.primaryGranulation),
			];
		}
	}

	override getDataForCopy(): CreateRecordProperties<GoalRecord> {
		const data = super.getDataForCopy();
		data.historyValues = copyObject(this.historyValues);
		return data;
	}

	static override modelName = ModelName.GOAL; // store record type
}
